import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { api } from "../utils/api";
import { getSocket } from "../utils/socket";
import { t } from "../utils/i18n";
import PageMeta from "../components/PageMeta";
import { useIsMobile } from "../hooks/useIsMobile";

// "Regarder ensemble" — lecteur YouTube synchronisé (voir plan : v1 YouTube uniquement, les
// plateformes DRM comme Netflix/HBO/Prime/Canal+ ne peuvent techniquement pas s'intégrer en
// iframe). Chat en direct non persisté — voir routes/watchRooms.ts et les événements socket
// `watch:*` (calqués sur `conversation_${id}`, voir socket/index.ts).
declare global {
  interface Window { YT?: any; onYouTubeIframeAPIReady?: () => void; }
}

let ytApiPromise: Promise<void> | null = null;
function loadYoutubeApi(): Promise<void> {
  if (window.YT?.Player) return Promise.resolve();
  if (ytApiPromise) return ytApiPromise;
  ytApiPromise = new Promise(resolve => {
    const previous = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => { previous?.(); resolve(); };
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    document.body.appendChild(tag);
  });
  return ytApiPromise;
}

// "Écouter ensemble" (Deezer) — voir le plan pour le contexte complet (pourquoi Deezer plutôt que
// Spotify, App ID requis). SDK ancien et peu documenté publiquement (portail développeur fermé
// sans compte) : les noms d'événements ci-dessous (`DZ.Event.Types.PLAYER_PLAY/PAUSE/POSITION`)
// viennent des exemples officiels disponibles publiquement, à reconfirmer empiriquement une fois
// DEEZER_APP_ID renseigné et testé avec un vrai compte — contrairement à l'intégration YouTube
// (API stable, déjà vérifiée en production), celle-ci porte un vrai risque résiduel de divergence.
let dzApiPromise: Promise<void> | null = null;
function loadDeezerApi(): Promise<void> {
  if ((window as any).DZ) return Promise.resolve();
  if (dzApiPromise) return dzApiPromise;
  dzApiPromise = new Promise(resolve => {
    const tag = document.createElement("script");
    tag.src = "https://cdns-files.deezer.com/js/min/dz.js";
    tag.onload = () => resolve();
    document.body.appendChild(tag);
  });
  return dzApiPromise;
}

type ChatLine = { fromUserId: string; fromName: string; text: string; at: string };
type Participant = { id: string; name: string; avatar_url?: string };

export default function WatchRoom() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, lang, user } = useApp();
  // Anciennement recalculé en ligne à chaque rendu (window.innerWidth < 900), donc jamais mis à
  // jour tant que rien d'autre ne redéclenchait un rendu — le hook partagé corrige aussi ce bug
  // au passage (vraie réactivité au redimensionnement, comme le reste de l'app).
  const isMobile = useIsMobile();
  const playerRef = useRef<any>(null);
  const playerElRef = useRef<HTMLDivElement | null>(null);
  // true juste après avoir appliqué un `watch:state` reçu à distance : le prochain onStateChange
  // qu'il déclenche mécaniquement ne doit pas être retraité comme un geste local, sinon on
  // rediffuse en boucle ce qu'on vient de recevoir (voir plan, anti-écho).
  const suppressNextStateChange = useRef(false);
  const lastKnownDeezerPosition = useRef(0);

  const [room, setRoom] = useState<any>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [chat, setChat] = useState<ChatLine[]>([]);
  const [chatInput, setChatInput] = useState("");
  const [notFound, setNotFound] = useState(false);
  const [deezerReady, setDeezerReady] = useState(false);
  const [deezerLoggedIn, setDeezerLoggedIn] = useState(false);
  const [isDeezerPlaying, setIsDeezerPlaying] = useState(false);
  const chatEndRef = useRef<HTMLDivElement | null>(null);
  const isDeezer = room?.provider === "deezer";

  // Chargement initial + rejoindre la salle (REST, pour être compté même si le socket met un
  // instant à se connecter) puis le canal temps réel.
  useEffect(() => {
    if (!id) return;
    api.get(`/watch-rooms/${id}`).then(data => {
      if (data?.error || !data?.room) { setNotFound(true); return; }
      setRoom(data.room);
      setParticipants(data.participants || []);
    });
    api.post(`/watch-rooms/${id}/join`, {}).catch(() => {});
  }, [id]);

  // Lecteur YouTube — créé une fois qu'on connaît le videoId, jamais recréé ensuite (les mises à
  // jour de position/lecture passent par les méthodes du player, pas par un remount).
  useEffect(() => {
    if (!room?.video_id || room.provider !== "youtube" || playerRef.current) return;
    let cancelled = false;
    loadYoutubeApi().then(() => {
      if (cancelled || !playerElRef.current) return;
      playerRef.current = new window.YT.Player(playerElRef.current, {
        videoId: room.video_id,
        playerVars: { autoplay: room.is_playing ? 1 : 0, start: Math.floor(room.position_seconds || 0) },
        events: {
          onReady: () => {
            if (room.position_seconds > 0) playerRef.current.seekTo(room.position_seconds, true);
          },
          onStateChange: (e: any) => {
            if (suppressNextStateChange.current) { suppressNextStateChange.current = false; return; }
            const socket = token && getSocket(token);
            if (!socket || !id) return;
            const positionSeconds = playerRef.current.getCurrentTime();
            if (e.data === window.YT.PlayerState.PLAYING) socket.emit("watch:play", { roomId: id, positionSeconds });
            else if (e.data === window.YT.PlayerState.PAUSED) socket.emit("watch:pause", { roomId: id, positionSeconds });
          },
        },
      });
    });
    return () => { cancelled = true; };
  }, [room, id, token]);

  // Lecteur Deezer — DZ.init n'est appelé qu'une fois (playerRef.current sert de garde ici
  // aussi, réutilisé pour porter l'objet DZ.player plutôt qu'un player YouTube). L'App ID doit
  // être renseigné (REACT_APP_DEEZER_APP_ID, voir plan) : tant que ce n'est pas le cas, la salle
  // s'affiche mais le lecteur ne peut pas s'initialiser — comportement volontaire, pas une erreur
  // à masquer.
  useEffect(() => {
    if (!room?.video_id || room.provider !== "deezer" || playerRef.current) return;
    const appId = process.env.REACT_APP_DEEZER_APP_ID;
    if (!appId) return;
    let cancelled = false;
    loadDeezerApi().then(() => {
      if (cancelled) return;
      const DZ = (window as any).DZ;
      DZ.init({
        appId,
        channelUrl: `${window.location.origin}/deezer-channel.html`,
        player: {
          onload: () => {
            playerRef.current = DZ.player;
            setDeezerReady(true);
            setIsDeezerPlaying(!!room.is_playing);
            DZ.player.playTracks([room.video_id], !!room.is_playing);
            if (room.position_seconds > 0 && room.duration_seconds) {
              DZ.player.seek((room.position_seconds / room.duration_seconds) * 100);
            }
          },
        },
      });
      DZ.Event.subscribe(DZ.Event.Types.PLAYER_PLAY, () => {
        if (suppressNextStateChange.current) { suppressNextStateChange.current = false; return; }
        const socket = token && getSocket(token);
        if (!socket || !id) return;
        socket.emit("watch:play", { roomId: id, positionSeconds: lastKnownDeezerPosition.current });
      });
      DZ.Event.subscribe(DZ.Event.Types.PLAYER_PAUSE, () => {
        if (suppressNextStateChange.current) { suppressNextStateChange.current = false; return; }
        const socket = token && getSocket(token);
        if (!socket || !id) return;
        socket.emit("watch:pause", { roomId: id, positionSeconds: lastKnownDeezerPosition.current });
      });
      // Position en secondes, dérivée du pourcentage — c'est le seul repère fiable documenté
      // publiquement pour ce SDK (voir commentaire plus haut sur le risque résiduel).
      DZ.Event.subscribe(DZ.Event.Types.PLAYER_POSITION, (position: number) => {
        if (room.duration_seconds) lastKnownDeezerPosition.current = (position / 100) * room.duration_seconds;
      });
    });
    return () => { cancelled = true; };
  }, [room, id, token]);

  // Canal temps réel de la salle — écoute pendant toute la durée de la page, quitte proprement
  // au démontage (autre participant informé, pas de fuite de listener sur reconnexion).
  useEffect(() => {
    if (!token || !id) return;
    const socket = getSocket(token);
    socket.emit("watch:join", id);

    const onState = ({ isPlaying, positionSeconds, fromUserId }: any) => {
      if (fromUserId === user?.id || !playerRef.current) return;
      suppressNextStateChange.current = true;
      if (isDeezer) {
        if (room?.duration_seconds) playerRef.current.seek((positionSeconds / room.duration_seconds) * 100);
        if (isPlaying) playerRef.current.play(); else playerRef.current.pause();
        setIsDeezerPlaying(isPlaying);
      } else {
        playerRef.current.seekTo(positionSeconds, true);
        if (isPlaying) playerRef.current.playVideo(); else playerRef.current.pauseVideo();
      }
    };
    const onParticipants = ({ userId, name, avatarUrl, joined }: any) => {
      setParticipants(prev => joined
        ? (prev.some(p => p.id === userId) ? prev : [...prev, { id: userId, name, avatar_url: avatarUrl }])
        : prev.filter(p => p.id !== userId));
    };
    const onChat = (line: ChatLine) => {
      setChat(prev => [...prev, line]);
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    };

    socket.on("watch:state", onState);
    socket.on("watch:participants_update", onParticipants);
    socket.on("watch:chat_message", onChat);
    return () => {
      socket.emit("watch:leave", id);
      socket.off("watch:state", onState);
      socket.off("watch:participants_update", onParticipants);
      socket.off("watch:chat_message", onChat);
    };
  }, [token, id, user?.id]);

  const sendChat = () => {
    if (!chatInput.trim() || !token || !id) return;
    const socket = getSocket(token);
    socket.emit("watch:chat_message", { roomId: id, text: chatInput.trim() });
    setChat(prev => [...prev, { fromUserId: user?.id || "", fromName: t(lang, "you") || "Toi", text: chatInput.trim(), at: new Date().toISOString() }]);
    setChatInput("");
  };

  if (notFound) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", flexDirection: "column", gap: 16 }}>
        <div style={{ fontSize: 15, color: "#666" }}>{t(lang, "watch_room_expired") || "Cette salle a expiré."}</div>
        <button onClick={() => navigate("/messages")} style={{ padding: "10px 20px", borderRadius: 12, border: "none", backgroundColor: "#0a0a0a", color: "white", fontWeight: 700, cursor: "pointer" }}>{t(lang, "cancel") || "Retour"}</button>
      </div>
    );
  }

  return (
    <>
      <PageMeta title={t(lang, "watch_together_title") || "Regarder ensemble"} description={room?.video_title || ""} />
      <div style={{ display: "flex", height: "100%", flexDirection: isMobile ? "column" : "row", backgroundColor: "#0a0a0a" }}>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
          {isDeezer ? (
            <div style={{ position: "relative", width: "100%", paddingTop: "56.25%", backgroundColor: "#181818" }}>
              <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16 }}>
                {room?.video_thumbnail_url && <img src={room.video_thumbnail_url} alt="" style={{ width: 140, height: 140, borderRadius: 12, objectFit: "cover", boxShadow: "0 12px 30px rgba(0,0,0,0.5)" }} />}
                <div style={{ display: "flex", gap: 14 }}>
                  <button
                    onClick={() => {
                      if (!playerRef.current) return;
                      const socket = token && getSocket(token);
                      if (!socket || !id) return;
                      const willPlay = !isDeezerPlaying;
                      setIsDeezerPlaying(willPlay);
                      if (willPlay) playerRef.current.play(); else playerRef.current.pause();
                      socket.emit(willPlay ? "watch:play" : "watch:pause", { roomId: id, positionSeconds: lastKnownDeezerPosition.current });
                    }}
                    disabled={!deezerReady}
                    style={{ width: 56, height: 56, borderRadius: "50%", border: "none", backgroundColor: "white", color: "#0a0a0a", fontSize: 20, cursor: deezerReady ? "pointer" : "default", opacity: deezerReady ? 1 : 0.5 }}
                  >
                    {isDeezerPlaying ? "❚❚" : "▶"}
                  </button>
                </div>
                {!deezerLoggedIn && (
                  <button
                    onClick={() => (window as any).DZ?.login(() => setDeezerLoggedIn(true))}
                    style={{ padding: "8px 16px", borderRadius: 20, border: "1px solid rgba(255,255,255,0.4)", backgroundColor: "transparent", color: "white", fontSize: 12.5, cursor: "pointer" }}
                  >
                    {t(lang, "watch_deezer_login")}
                  </button>
                )}
                {!process.env.REACT_APP_DEEZER_APP_ID && (
                  <div style={{ color: "#c0392b", fontSize: 12, textAlign: "center", maxWidth: 280 }}>App ID Deezer non configuré, lecture indisponible.</div>
                )}
              </div>
            </div>
          ) : (
            <div style={{ position: "relative", width: "100%", paddingTop: "56.25%", backgroundColor: "black" }}>
              <div ref={playerElRef} style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} />
            </div>
          )}
          <div style={{ padding: "14px 20px", color: "white" }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>{room?.video_title}</div>
            <div style={{ display: "flex", gap: -8 }}>
              {participants.map((p, i) => (
                <div key={p.id} title={p.name} style={{ width: 26, height: 26, borderRadius: "50%", border: "2px solid #0a0a0a", marginLeft: i > 0 ? -8 : 0, backgroundColor: "#333", overflow: "hidden", flexShrink: 0 }}>
                  {p.avatar_url && <img src={p.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
                </div>
              ))}
              <span style={{ fontSize: 12, color: "#999", marginLeft: 12, alignSelf: "center" }}>{participants.length} {t(lang, "watch_room_participants") || "participants"}</span>
            </div>
          </div>
        </div>

        <div style={{ width: isMobile ? "100%" : 320, flexShrink: 0, backgroundColor: "white", display: "flex", flexDirection: "column", borderLeft: isMobile ? "none" : "1px solid #222" }}>
          <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
            {chat.map((line, i) => (
              <div key={i} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#999" }}>{line.fromUserId === user?.id ? (t(lang, "you") || "Toi") : line.fromName}</div>
                <div style={{ fontSize: 13.5 }}>{line.text}</div>
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>
          <div style={{ display: "flex", gap: 8, padding: 12, borderTop: "1px solid #F0F0F0" }}>
            <input
              value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyDown={e => e.key === "Enter" && sendChat()}
              placeholder={t(lang, "watch_room_chat_placeholder") || "Message..."}
              style={{ flex: 1, padding: "10px 14px", borderRadius: 20, border: "1px solid #E0E0E0", fontSize: 13.5, outline: "none" }}
            />
            <button onClick={sendChat} style={{ border: "none", background: "none", cursor: "pointer", color: "#0a0a0a", fontWeight: 700, fontSize: 13 }}>{t(lang, "msg_send") || "Envoyer"}</button>
          </div>
        </div>
      </div>
    </>
  );
}
